import { useState } from "react";
import { Search } from 'lucide-react';
import PieChart from "./PieChart.jsx";
import { formatCurrency, formatNumber, formatUnits } from "./api.js";
import { DemoBanner } from "./ui/index.jsx";

// ✅ Chart color palette used across drill-down views
const CHART_COLORS = Array.from({ length: 1750 }, (_, i) => {
  const hue = (i * 137.508) % 360; // Golden angle for even distribution
  const saturation = 65 + (i % 3) * 10; // 65%, 75%, 85%
  const lightness = 45 + (i % 4) * 8;   // 45%, 53%, 61%, 69%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
});

/**
 * Optimized Donut Chart
 * Fixes "vanishing colors" on thin slices by using dynamic stroke widths.
 */
function DonutChartWithTooltip({ data, centerLabel, centerValue }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Fallback palette in case colors are missing in the data
  const FALLBACK_PALETTE = [
    "#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", 
    "#3b82f6", "#ec4899", "#14b8a6", "#f97316", "#0ea5e9"
  ];

  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const size = 220;
  const outerRadius = 95;
  const innerRadius = 60;
  const cx = size / 2;
  const cy = size / 2;

  let cumulativeAngle = -90;

  // 1. Path and Data Calculation
  const slices = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    const rawAngle = (item.value / total) * 360;
    
    // SVG paths fail if angle is exactly 360. 
    const angle = rawAngle >= 360 ? 359.99 : rawAngle; 
    
    const startAngle = cumulativeAngle;
    const endAngle = cumulativeAngle + angle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = cx + outerRadius * Math.cos(startRad);
    const y1 = cy + outerRadius * Math.sin(startRad);
    const x2 = cx + outerRadius * Math.cos(endRad);
    const y2 = cy + outerRadius * Math.sin(endRad);

    const x3 = cx + innerRadius * Math.cos(endRad);
    const y3 = cy + innerRadius * Math.sin(endRad);
    const x4 = cx + innerRadius * Math.cos(startRad);
    const y4 = cy + innerRadius * Math.sin(startRad);

    const largeArc = angle > 180 ? 1 : 0;

    const path = `
      M ${x1} ${y1}
      A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2}
      L ${x3} ${y3}
      A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
      Z
    `;

    const midAngle = (startAngle + endAngle) / 2;
    const midRad = (midAngle * Math.PI) / 180;
    const labelRadius = (outerRadius + innerRadius) / 2;
    const labelX = cx + labelRadius * Math.cos(midRad);
    const labelY = cy + labelRadius * Math.sin(midRad);

    cumulativeAngle = endAngle;

    return { 
      ...item, 
      path, 
      percentage, 
      labelX, 
      labelY, 
      index,
      color: item.color || FALLBACK_PALETTE[index % FALLBACK_PALETTE.length]
    };
  });

  // 2. Formatting Helpers
  const formatCurrency = (val) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
  
  const formatNumber = (val) => 
    new Intl.NumberFormat('en-US').format(val);

  return (
    <div className="relative flex flex-col items-center w-full">
      <div className="relative">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          onMouseLeave={() => setHoveredIndex(null)}
          className="drop-shadow-sm"
        >
          {slices.map((slice) => {
            // ✅ DYNAMIC STROKE WIDTH:
            // If many slices exist, we use a hairline stroke so colors aren't hidden.
            const strokeWidth = data.length > 30 ? "0.2" : data.length > 10 ? "0.8" : data.length > 1 ? "2" : "0";

            return (
              <g key={slice.index}>
                <path
                  d={slice.path}
                  fill={slice.color}
                  stroke="white"
                  strokeWidth={strokeWidth}
                  className="cursor-pointer transition-all duration-300"
                  style={{
                    opacity: hoveredIndex === null || hoveredIndex === slice.index ? 1 : 0.4,
                    transform: hoveredIndex === slice.index ? 'scale(1.03)' : 'scale(1)',
                    transformOrigin: 'center',
                  }}
                  onMouseEnter={(e) => {
                    setHoveredIndex(slice.index);
                    setMousePos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                />
                
                {/* Only show percentage on large enough slices */}
                {slice.percentage > 5 && (
                  <text
                    x={slice.labelX}
                    y={slice.labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize="10"
                    fontWeight="800"
                    pointerEvents="none"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                  >
                    {slice.percentage.toFixed(1)}%
                  </text>
                )}
              </g>
            );
          })}

          {/* Center Text (Labels) */}
          <text 
            x={cx} 
            y={cy - 8} 
            textAnchor="middle" 
            fontSize="10" 
            fill="#94a3b8" 
            fontWeight="700" 
            className="uppercase tracking-widest"
          >
            {centerLabel}
          </text>
          <text 
            x={cx} 
            y={cy + 12} 
            textAnchor="middle" 
            fontSize="16" 
            fill="#1e293b" 
            fontWeight="800"
          >
            {centerValue}
          </text>
        </svg>
      </div>

      {/* Floating Tooltip */}
      {hoveredIndex !== null && (
        <div
          className="fixed z-[9999] pointer-events-none bg-white/95 backdrop-blur shadow-2xl rounded-xl px-4 py-3 border border-slate-200 ring-1 ring-black/5"
          style={{
            left: mousePos.x + 15,
            top: mousePos.y + 15,
            minWidth: '200px',
          }}
        >
          <div className="flex items-center gap-2 mb-2 border-b border-slate-100 pb-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: slices[hoveredIndex].color }} 
            />
            <div className="text-xs font-black text-slate-600 uppercase tracking-tight truncate">
              {slices[hoveredIndex].label}
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-slate-500 font-bold uppercase">Revenue</span>
              <span className="text-sm font-bold text-slate-900">
                {formatCurrency(slices[hoveredIndex].revenue || slices[hoveredIndex].value)}
              </span>
            </div>
            {slices[hoveredIndex].unitsSold !== undefined && (
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-slate-500 font-bold uppercase">Units</span>
                <span className="text-sm font-bold text-slate-900">
                  {formatNumber(slices[hoveredIndex].unitsSold)}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center pt-1 mt-1 border-t border-slate-50">
              <span className="text-[10px] text-slate-500 font-bold uppercase">Share</span>
              <span className="text-sm font-black text-indigo-600">
                {slices[hoveredIndex].percentage.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Sales by Region
 * -----------------------------------------------------------
 *   • Pie   → grouped by Country
 *   • Table → Country rows with drill-down to Province/State
 *
 * Each region shows TWO percentages:
 *   – % of the country (local share)
 *   – % of global sales
 *
 * Client example: "Quebec = 22% in Canada, 9% globally"
 */
export default function SalesByRegionSection({ query, COUNTRY_NAMES, COUNTRY_STATES }) {
  const [openCountry, setOpenCountry] = useState(null);

  if (query.loading) return <Skeleton />;

  const apiCountries = query.data?.countries || [];
  const isDemo       = apiCountries.length === 0;
  const data         = query?.data || [];
  const countries    = data.countries || [];
  const totals       = data.totals    || {};

  if (!countries.length) {
    return <EmptyState label="No sales with country/region data found for this filter set." />;
  }

return (
  <>
    <div className="p-6 space-y-4">
      {/*isDemo && <DemoBanner />*/}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">

        {/* ---- Country pie (STICKY) ---- */}
        <div className="lg:sticky lg:top-6 lg:self-start flex flex-col items-center justify-center rounded-xl bg-slate-50 p-4 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto">
          <DonutChartWithTooltip
            centerLabel="Countries"
            centerValue={formatNumber(countries.length)}
            data={countries.map((c) => ({
              label: c.country,
              value: c.revenue,
              color: c.color,
            }))}
          />
        </div>

        {/* ---- Country table with region drill ---- */}
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="w-8 px-2 py-2.5" />
                <Th>#</Th>
                <Th>Country</Th>
                <Th align="right">Regions</Th>
                <Th align="right">Orders</Th>
                <Th align="right">Revenue</Th>
                <Th align="right">Units</Th>                
                <Th align="right">% Global</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {countries
              // .sort((a, b) => a.country.localeCompare(b.country))
              .map((c) => {
                const isOpen = openCountry === c.country;
                return (
                  <CountryRow
                    key={c.country}
                    row={c}
                    isOpen={isOpen}
                    onToggle={() =>
                      setOpenCountry(isOpen ? null : c.country)
                    }
                    COUNTRY_NAMES={COUNTRY_NAMES}
                    COUNTRY_STATES={COUNTRY_STATES}
                  />
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-slate-200 bg-slate-50">
              <tr>
                <td />
                <td />
                <td className="px-4 py-2.5 font-bold">Global Total</td>                
                <td className="px-4 py-2.5 text-right font-bold">
                  {formatNumber(totals.regionCount || 0)}
                </td>
                <td className="px-4 py-2.5 text-right font-bold">
                  {formatNumber(totals.orders || 0)}
                </td>
                <td className="px-4 py-2.5 text-right font-bold">
                  {formatCurrency(totals.revenue || 0)}
                </td>                
                <td className="px-4 py-2.5 text-right font-bold">
                  {formatUnits(totals.unitsSold || 0)}
                </td>
                <td className="px-4 py-2.5 text-right font-bold">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>

      </div>
    </div>
  </>
);
}

function CountryRow({ row, isOpen, onToggle, COUNTRY_NAMES, COUNTRY_STATES }) {

  const [stateSearch, setStateSearch] = useState({});

  const handleStateSearch = (countryCode, value) => {
    setStateSearch(prev => ({ ...prev, [countryCode]: value }));
  };

  return (
    <>
      <tr onClick={onToggle} className="cursor-pointer transition hover:bg-slate-50/60">
        <td className="px-2 py-3 text-center text-slate-400">
          <span className={`inline-block transition-transform ${isOpen ? "rotate-90" : ""}`}>▸</span>
        </td>
        <td className="px-4 py-3">
          <RankBadge n={row.rank} />
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: row.color }} />
            <span className="font-semibold text-slate-900">{COUNTRY_NAMES[row.country] || row.country}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-right">
          <span className="inline-flex min-w-[2.5rem] items-center justify-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-bold text-indigo-700">
            {row.regionCount}
          </span>
        </td>
        <td className="px-4 py-3 text-right">
          <span className="inline-flex min-w-[2.5rem] items-center justify-center rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-bold text-indigo-700">
            {formatNumber(row.orders)}
          </span>
        </td>        
        <td className="px-4 py-3 text-right font-semibold">{formatCurrency(row.revenue)}</td>        
        <td className="px-4 py-3 text-right">{formatUnits(row.unitsSold)}</td>
        <td className="px-4 py-3 text-right"><ShareBar pct={row.sharePctGlobal} color={row.color} /></td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={7} className="bg-slate-50 px-4 py-3">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">

              {/* Header */}
              <div className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                Province / State breakdown for <strong>{row.country}</strong>
                <span className="ml-2 text-slate-400">
                  ({(row.regions || []).length} regions)
                </span>
              </div>

              {/* Search Bar */}
              <div className="border-b border-slate-100 bg-white px-3 py-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder={`Search states in ${COUNTRY_NAMES[row.country] || row.country}...`}
                    value={stateSearch[row.country] || ''}
                    onChange={(e) => handleStateSearch(row.country, e.target.value)}
                    className="w-full rounded-md border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-8 text-sm
                              text-slate-700 placeholder:text-slate-400
                              focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100
                              transition-colors"
                  />
                  {stateSearch[row.country] && (
                    <button
                      onClick={() => handleStateSearch(row.country, '')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400
                                hover:text-slate-600 transition-colors"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Scrollable Table Container */}
              <div className="max-h-72 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-50">
                <table className="w-full text-sm">
                  <thead className="bg-white sticky top-0 z-10 shadow-[0_1px_0_0_rgb(241,245,249)]">
                    <tr className="border-b border-slate-100">
                      <Th align="center">#</Th>
                      <Th>Region</Th>
                      <Th>Orders</Th>
                      <Th align="right">Revenue</Th>
                      <Th align="right">Units</Th>                      
                      <Th align="right">% in {row.country}</Th>
                      <Th align="right">% Global</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(() => {
                      const searchTerm = (stateSearch[row.country] || '').toLowerCase();
                      const filtered = (row.regions || []).filter((r) => {
                        if (!searchTerm) return true;
                        const stateName = COUNTRY_STATES[row.country]?.find(s => s.code === r.region)?.name || r.region;
                        return (
                          stateName.toLowerCase().includes(searchTerm) ||
                          r.region.toLowerCase().includes(searchTerm)
                        );
                      });

                      if (filtered.length === 0) {
                        return (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                              <div className="flex flex-col items-center gap-1">
                                <Search className="h-5 w-5 text-slate-300" />
                                <span className="text-sm">No states found matching "{stateSearch[row.country]}"</span>
                                <button
                                  onClick={() => handleStateSearch(row.country, '')}
                                  className="mt-1 text-xs text-blue-500 hover:text-blue-600 hover:underline"
                                >
                                  Clear search
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      return filtered.sort((a, b) => String(a ?? '').localeCompare(String(b ?? ''))).map((r, index) => (
                        <tr key={r.region} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-4 py-2 text-center text-slate-400 text-xs tabular-nums">
                            {index + 1}
                          </td>
                          <td className="px-4 py-2 font-medium text-slate-900">
                            {(() => {
                              //const stateName = COUNTRY_STATES[row.country]?.find(s => s.code === r.region)?.name || r.region;
                              const stateName = r.region;
                              if (!searchTerm) return stateName;

                              // Highlight matching text
                              const idx = stateName.toLowerCase().indexOf(searchTerm);
                              if (idx === -1) return stateName;

                              return (
                                <>
                                  {stateName.slice(0, idx)}
                                  <mark className="bg-yellow-100 text-yellow-900 rounded px-0.5">
                                    {stateName.slice(idx, idx + searchTerm.length)}
                                  </mark>
                                  {stateName.slice(idx + searchTerm.length)}
                                </>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-2 text-right font-semibold tabular-nums">{formatNumber(r.orders)}</td>
                          <td className="px-4 py-2 text-right font-semibold tabular-nums">{formatCurrency(r.revenue)}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{formatUnits(r.unitsSold)}</td>                        
                          <td className="px-4 py-2 text-right">
                            <PctChip pct={r.sharePctInCountry} color={row.color} />
                          </td>
                          <td className="px-4 py-2 text-right">
                            <PctChip pct={r.sharePctGlobal} color="#64748b" />
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Footer with count */}
              {stateSearch[row.country] && (
                <div className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-xs text-slate-400">
                  Showing{' '}
                  {(row.regions || []).filter((r) => {
                    const searchTerm = (stateSearch[row.country] || '').toLowerCase();
                    const stateName = COUNTRY_STATES[row.country]?.find(s => s.code === r.region)?.name || r.region;
                    return stateName.toLowerCase().includes(searchTerm) || r.region.toLowerCase().includes(searchTerm);
                  }).length}{' '}
                  of {(row.regions || []).length} regions
                </div>
              )}

            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* -- primitives (kept local so this file is self-contained) -- */
function Th({ children, align }) {
  return (
    <th className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${align === "right" ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}

function RankBadge({ n }) {
  const cls =
    n === 1 ? "bg-amber-100 text-amber-700" :
    n === 2 ? "bg-slate-200 text-slate-700" :
    n === 3 ? "bg-orange-100 text-orange-700" :
              "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold ${cls}`}>
      {n}
    </span>
  );
}

function ShareBar({ pct, color }) {
  return (
    <div className="inline-flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-200">
        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="w-12 text-right text-xs font-semibold text-slate-700">{Number(pct).toFixed(1)}%</span>
    </div>
  );
}

function PctChip({ pct, color }) {
  return (
    <span
      className="inline-flex rounded-md px-2 py-0.5 text-[11px] font-bold"
      style={{ backgroundColor: `${color}18`, color }}
    >
      {Number(pct || 0).toFixed(1)}%
    </span>
  );
}

function Skeleton() {
  return (
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
      <div className="flex items-center justify-center rounded-xl bg-slate-50 p-10">
        <div className="h-44 w-44 animate-pulse rounded-full bg-slate-200" />
      </div>
      <div className="space-y-2 rounded-xl border border-slate-200 p-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-9 w-full animate-pulse rounded bg-slate-100" />
        ))}
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-sm text-slate-500">
      {label}
    </div>
  );
}